import {
  WORKFLOW_ACTION_BINDINGS_MAX,
  parseWorkflowActionBindings,
  withWorkflowActionBinding,
  withoutWorkflowActionBinding,
  workflowActionBindingSchema,
} from '@erp/workflows';
import { TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { useListActionWorkflowOptions } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { workflowActionRows } from './workflowActionToolRows';

/**
 * Bindings live on `config.workflowActions`. The Tools tab saves the catalog
 * list as `{ toolType }` only, so a per-tool config on that list would be
 * dropped. Every task this agent runs receives the published actions here.
 */
export function WorkflowActionToolsSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const gt = useGT();
  const { data, isLoading, isError } = useListActionWorkflowOptions();
  const bindings = parseWorkflowActionBindings(config);
  const catalogResolved = data !== undefined;
  const { rows, available } = workflowActionRows(
    bindings,
    (data?.workflows ?? []).map((workflow) => ({
      workflowId: workflow.workflowId,
      name: workflow.name,
      description: workflow.description,
    })),
    { catalogResolved },
  );
  const atCap = bindings.length >= WORKFLOW_ACTION_BINDINGS_MAX;
  const showPicker = !atCap && !isLoading && !isError && available.length > 0;

  function addWorkflow(workflowId: string) {
    if (!workflowActionBindingSchema.safeParse({ workflowId }).success) return;
    onChange(withWorkflowActionBinding(config, workflowId));
  }

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold">
          <T context="agent tools tab section for workflow actions the agent can call">
            Workflow actions
          </T>
        </h4>
        <Badge variant="secondary" className="text-xs">
          {bindings.length}
        </Badge>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        <T context="explains that workflow action tools are available on every task this agent runs">
          Every task this agent runs can call these.
        </T>
      </p>

      {isError && (
        <p className="text-xs text-destructive">
          <T context="error when the workflow action picker fails to load">
            Failed to load workflow actions.
          </T>
        </p>
      )}

      {rows.length === 0 &&
        !isLoading &&
        !isError &&
        available.length === 0 && (
          <p className="text-xs text-muted-foreground">
            <T context="empty state when no published workflow action can be attached to an agent">
              Publish a workflow action, then attach it here.
            </T>
          </p>
        )}

      <ul className="space-y-1">
        {rows.map((row) => {
          const label =
            row.name ??
            (row.availability === 'pending'
              ? gt('Saved workflow action', {
                  context:
                    'workflow action binding shown before the catalog has loaded',
                })
              : gt('Unavailable', {
                  context:
                    'workflow action binding whose published workflow is gone',
                }));
          return (
            <li
              key={row.workflowId}
              className="flex items-start gap-1 rounded-md border px-2 py-1.5"
              title={row.workflowId}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">{label}</p>
                {row.description && (
                  <p className="truncate text-[11px] text-muted-foreground">
                    {row.description}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label={gt('Remove {name}', {
                  context: 'remove a workflow action tool from an agent',
                  name: label,
                })}
                onClick={() =>
                  onChange(withoutWorkflowActionBinding(config, row.workflowId))
                }
              >
                <TrashIcon className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </li>
          );
        })}
      </ul>

      {atCap && (
        <p className="text-[11px] text-muted-foreground">
          <T context="cap on how many workflow actions can be attached to one agent">
            This agent already has the maximum of 20 workflow actions.
          </T>
        </p>
      )}

      {showPicker && (
        <Select
          key={bindings.map((binding) => binding.workflowId).join(',')}
          onValueChange={addWorkflow}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue
              placeholder={gt('Add a workflow action', {
                context:
                  'placeholder to attach a workflow action tool to an agent',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {available.map((choice) => (
              <SelectItem key={choice.workflowId} value={choice.workflowId}>
                {choice.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
